import { useCallback, useRef, useState } from 'react';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { BulkAction, BulkActionItem, BulkActionProgress } from './types';
import { actionThunkByAction, getItemsWithApplicability, INITIAL_PROGRESS } from './utils';

export function useBulkFlowActions(onComplete?: () => void) {
  const dispatch = useAppDispatch();
  const [progress, setProgress] = useState<BulkActionProgress>(INITIAL_PROGRESS);
  const cancelRef = useRef(false);

  const run = useCallback(
    async (action: BulkAction, items: BulkActionItem[]) => {
      if (!items.length) return;

      cancelRef.current = false;
      const itemsWithApplicability = getItemsWithApplicability(action, items);
      const applicable = itemsWithApplicability.filter(
        ({ applicability }) => applicability.apply,
      ).length;
      setProgress({
        ...INITIAL_PROGRESS,
        open: true,
        action,
        total: items.length,
        applicable,
        status: 'running',
      });

      for (const { item, applicability } of itemsWithApplicability) {
        if (cancelRef.current) break;

        if (!applicability.apply) {
          setProgress((prev) => ({
            ...prev,
            completed: prev.completed + 1,
            skipped: prev.skipped + 1,
            log: [
              ...prev.log,
              { name: item.name, outcome: 'skipped', reason: applicability.reason },
            ],
          }));
          continue;
        }

        setProgress((prev) => ({ ...prev, currentName: item.name }));

        try {
          await dispatch(actionThunkByAction[action](item.flowNodeId));
          setProgress((prev) => ({
            ...prev,
            completed: prev.completed + 1,
            succeeded: prev.succeeded + 1,
            log: [...prev.log, { name: item.name, outcome: 'ok' }],
          }));
        } catch (error: any) {
          setProgress((prev) => ({
            ...prev,
            completed: prev.completed + 1,
            failed: prev.failed + 1,
            log: [
              ...prev.log,
              { name: item.name, outcome: 'failed', reason: error?.message || 'Request failed' },
            ],
          }));
        }
      }

      setProgress((prev) => ({
        ...prev,
        status: cancelRef.current ? 'cancelled' : 'done',
      }));
    },
    [dispatch],
  );

  const cancel = useCallback(() => {
    cancelRef.current = true;
    setProgress((prev) => (prev.status === 'running' ? { ...prev, status: 'cancelling' } : prev));
  }, []);

  const close = useCallback(() => {
    setProgress(INITIAL_PROGRESS);
    onComplete?.();
  }, [onComplete]);

  return { progress, run, cancel, close };
}

export default useBulkFlowActions;
